/**
 * Safe-area guide (W2-02; DESIGN.md §4.8): the largest 16:9, 4:3 or 3:2 rectangle centred in the chart viewport
 * (RPE's 3:2 editor frame, or what a narrower screen keeps in view), with the outside lightly shaded.
 */

import type { AppServices } from '../../../app/registry/types';
import type { OverlayDrawContext, PreviewOverlayProvider } from '../../../render/types';
import { m } from '../preview.messages';
import { safeAreaAspect } from '../prefs';
import { dp, drawLabel } from './shared';
import { overlayTheme } from './theme';

/** The guide rectangle of aspect `a` inside a viewport (device px). */
export function safeRect(
  vp: { vx: number; vy: number; W: number; H: number },
  a: number,
): readonly [number, number, number, number] {
  const ar = vp.H > 0 ? vp.W / vp.H : a;
  let w = vp.W;
  let h = vp.H;
  if (a < ar) w = vp.H * a;
  else h = vp.W / a;
  return [vp.vx + (vp.W - w) / 2, vp.vy + (vp.H - h) / 2, w, h];
}

export function createSafeAreasOverlay(services: AppServices): PreviewOverlayProvider {
  return {
    id: 'preview.safeAreas',
    order: 10,
    draw(c: OverlayDrawContext) {
      const kind = services.editor.getState().preview.safeAreas;
      const a = safeAreaAspect(kind);
      if (a === null) return;
      const theme = overlayTheme();
      const vp = c.viewport;
      const [x, y, w, h] = safeRect(vp, a);
      const ctx = c.ctx;
      ctx.save();
      // Shade the part of the viewport outside the guide.
      ctx.beginPath();
      ctx.rect(vp.vx, vp.vy, vp.W, vp.H);
      ctx.rect(x, y, w, h);
      ctx.fillStyle = theme.surface;
      ctx.globalAlpha = 0.28;
      ctx.fill('evenodd');
      ctx.globalAlpha = 1;
      ctx.strokeStyle = theme.guide;
      ctx.lineWidth = dp(c, 1.5);
      ctx.setLineDash([dp(c, 8), dp(c, 5)]);
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
      drawLabel(c, services.i18n.t(m.safeAreaLabel, { aspect: kind }), x + dp(c, 4), y + dp(c, 4), {
        fg: theme.text,
        bg: theme.surface,
        border: theme.guide,
      });
    },
  };
}
